/**
 * Browser-side state for the exam wrapper (pedagogy spec §7.3.8):
 *
 * - the result page's unsaved reflection, mirrored to `sessionStorage` under
 *   `mf.exam.reflection.<sessionId>` and used on mount only when it is newer
 *   than the saved reflection;
 * - the "Dölj" flag of the last-plan note on the exam page,
 *   `mf.exam.lastPlan.hidden.<sessionId>`.
 *
 * Every access is guarded: without storage (private mode, a full quota) the
 * page loses the convenience, never its state.
 */
import { isExamCause } from "@shared/examWrapper";
import type { ReflectionDto } from "../types";
import type { CauseState } from "./model";

const DRAFT_PREFIX = "mf.exam.reflection.";
const HIDDEN_PREFIX = "mf.exam.lastPlan.hidden.";

export interface ReflectionDraft {
  causes: CauseState;
  plan: string;
  /** `Date.now()` of the last edit, compared with the saved reflection's `savedAt`. */
  editedAt: number;
}

export function saveReflectionDraft(sessionId: string, draft: ReflectionDraft): void {
  try {
    window.sessionStorage.setItem(`${DRAFT_PREFIX}${sessionId}`, JSON.stringify(draft));
  } catch {
    /* storage unavailable — the page still works from React state */
  }
}

export function loadReflectionDraft(sessionId: string): ReflectionDraft | null {
  let parsed: unknown;
  try {
    const raw = window.sessionStorage.getItem(`${DRAFT_PREFIX}${sessionId}`);
    if (!raw) return null;
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const { causes, plan, editedAt } = parsed as Record<string, unknown>;
  if (typeof plan !== "string" || typeof editedAt !== "number") return null;
  const cleaned: CauseState = {};
  if (causes && typeof causes === "object") {
    for (const [questionId, list] of Object.entries(causes)) {
      if (!Array.isArray(list)) continue;
      const known = list.filter(isExamCause);
      if (known.length > 0) cleaned[questionId] = known;
    }
  }
  return { causes: cleaned, plan, editedAt };
}

export function clearReflectionDraft(sessionId: string): void {
  try {
    window.sessionStorage.removeItem(`${DRAFT_PREFIX}${sessionId}`);
  } catch {
    /* ignore */
  }
}

/** Sign-out / account deletion: no reflection draft or hidden flag may outlive the signed-in user on this tab. */
export function clearAllReflectionDrafts(): void {
  try {
    const storage = window.sessionStorage;
    for (let i = storage.length - 1; i >= 0; i--) {
      const name = storage.key(i);
      if (name?.startsWith(DRAFT_PREFIX) || name?.startsWith(HIDDEN_PREFIX)) {
        storage.removeItem(name);
      }
    }
  } catch {
    /* ignore */
  }
}

/** What the result page starts from: a newer draft, else the saved reflection, else nothing. */
export function initialReflection(
  sessionId: string,
  saved: ReflectionDto | null,
): { causes: CauseState; plan: string } {
  const draft = loadReflectionDraft(sessionId);
  if (draft && draft.editedAt > (saved?.savedAt ?? 0)) {
    return { causes: draft.causes, plan: draft.plan };
  }
  return { causes: { ...(saved?.causes ?? {}) }, plan: saved?.plan ?? "" };
}

export function isLastPlanHidden(sessionId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${HIDDEN_PREFIX}${sessionId}`) === "1";
  } catch {
    return false;
  }
}

export function hideLastPlan(sessionId: string): void {
  try {
    window.sessionStorage.setItem(`${HIDDEN_PREFIX}${sessionId}`, "1");
  } catch {
    /* ignore — the note hides for this render anyway */
  }
}
